import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Checkbox, FormControlLabel, Alert, Container, CircularProgress, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import SimpleNavbar from '../shared/SimpleNavbar';

const TermsAcceptance = () => {
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, loading: authLoading, updateUser } = useAuth();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleAccept = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/profile/accept-terms');
      updateUser({ termsAccepted: true });
      if (user && user.isSuperAdmin) {
        navigate('/super-admin');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept terms.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking auth status
  if (authLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <SimpleNavbar />
        <Container component="main" maxWidth="xs">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  // Don't render anything if not authenticated (will be redirected by useEffect)
  if (!user) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <SimpleNavbar />
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" gutterBottom align="center">
              Terms and Conditions
            </Typography>
            
            <Box sx={{ 
              maxHeight: 300, 
              overflowY: 'auto', 
              border: 1, 
              borderColor: 'divider', 
              p: 2, 
              mb: 2,
              borderRadius: 1
            }}>
              <Typography variant="body2">
                Please read and accept the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions and Privacy Policy</a> to continue using the system.
              </Typography>
            </Box>
            
            <FormControlLabel
              control={<Checkbox checked={accept} onChange={e => setAccept(e.target.checked)} />}
              label="I accept the Terms and Conditions and Privacy Policy"
            />
            
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!accept || loading}
              onClick={handleAccept}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Accept and Continue'}
            </Button>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAcceptance; 